import { useCallback } from "react"
import { Edge, InternalNode, XYPosition, useReactFlow } from "@xyflow/react"
import { atom, useAtom, useSetAtom, useStore } from "jotai"
import { duplicateNodes } from "./ContextNodeMenu/duplicateNodes"
import { AppNode, EdgeData } from "../types"
import { atomWithStorage } from "jotai/utils"

/** Persistent storage for copy/paste nodes within or between graphs */
export const copiedNodesAtom = atomWithStorage<InternalNode<AppNode>[]>("graph-copied-nodes", [])

/** Rename pasted nodes in dialog window */
export const nodesToDuplicateAtom = atom<{
  nodes: AppNode[]
  oldToNewIdMap: Map<string, string>
}>({ nodes: [], oldToNewIdMap: new Map() })

if (process.env.NODE_ENV !== "production") {
  copiedNodesAtom.debugLabel = "copiedNodesAtom"
  nodesToDuplicateAtom.debugLabel = "nodesToDuplicateAtom"
}

export function useCopyPaste(onCloseClick?: () => void) {
  const reactFlowInstance = useReactFlow<AppNode, Edge<EdgeData>>()
  // useAtom instead of useSetAtom to keep this component subscribed to the atom.
  // Without a subscriber, Jotai drops the in-memory atom state on unmount, losing
  // copied nodes when navigating between graphs.
  const [, setAllNodes] = useAtom(copiedNodesAtom)
  const store = useStore()
  const setNodesToDuplicate = useSetAtom(nodesToDuplicateAtom)

  const handleCopyNodes = useCallback(() => {
    const nodes = structuredClone(
      reactFlowInstance.getNodes().map((node) => reactFlowInstance.getInternalNode(node.id)!),
    )

    setAllNodes(nodes)

    if (import.meta.env.VITE_COPY_NODES_TO_CLIPBOARD === "true") {
      navigator.clipboard.writeText(JSON.stringify(nodes)).catch(() => {
        // Clipboard write can fail (e.g. missing permission, insecure context) —
        // localStorage copy/paste still works, so this is non-fatal.
      })
    }

    onCloseClick?.()
  }, [reactFlowInstance, setAllNodes, onCloseClick])

  const handlePasteNodes = useCallback(
    (position: XYPosition) => {
      // Read the atom value directly from the Jotai store at call time.
      // This avoids a stale closure: if we closed over `allNodes` from useAtom,
      // the handler registered in KeymapContext would hold the value from the
      // previous render and only update after the full React re-render cycle
      // (setAllNodes → re-render → new useCallback → new KeyBinding effect →
      // new regKey). In production that window is large enough to cause
      // Ctrl+V to paste nothing on the first press after Ctrl+C.
      const allNodes = store.get(copiedNodesAtom)
      const allTargetNodes = reactFlowInstance.getNodes()

      const { nodes, oldToNewIdMap } = duplicateNodes({
        allSourceNodes: allNodes,
        allTargetNodes,
        position,
        reactFlowInstance,
      })

      setNodesToDuplicate({ nodes, oldToNewIdMap })
    },
    [store, reactFlowInstance, setNodesToDuplicate],
  )

  return { handleCopyNodes, handlePasteNodes }
}
